'use client';

import {
  FC,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';

import {
  notifyStoreCookieChanged,
  useActiveHeaderSlice,
  useCookies,
  useFeaturedFlag,
  useIsServerSide,
  useShoppingMode,
} from '@/hooks';
import { useAnalytics, useEcomStoreSelector, useUiStoreSelector } from '@/data';
import { ILocalAddress, useData } from '@/components';
import { TreezStore } from '@/types';
import { CookiesNames } from '@/constants';
import {
  addStoreTerm,
  IAgeTermsData,
  isStoreAgeGateSatisfied,
  resolveAgeGateContent,
  resolveStoreTermsMaxAge,
  StoreTermsMap,
} from 'app/_components/AgeGate/utils';
import {
  getDeliveryProviderId,
  getDeliveryScheduleByAddress,
  getExternalStores,
  getOverlappingDeliveryProviderIds,
  mergeStoresWithFlag,
  storeHasConfiguredDeliveryZones,
} from '@/utils';

import { StoreLocatorContext } from './context';

import style from './storelocator.module.scss';
interface IStoreLocatorProviderProps {
  children: ReactNode;
  fullWidth?: boolean;
}
const StoreLocatorProvider: FC<IStoreLocatorProviderProps> = ({
  children,
  fullWidth,
}) => {
  const { treezStores, storeSelector, store, ageGate } = useData();
  const activeSlice = useActiveHeaderSlice();
  const externalStores = getExternalStores();
  const { measureSurfsideChangeLocation } = useAnalytics();
  const { forceToSelectStore, forceToSelectStoreOnEachVisit } =
    useFeaturedFlag();
  const { setCookieValue, setCookieObject, getCookieObject } = useCookies();
  const { isServerSide } = useIsServerSide();
  const prevCommittedStoreRef = useRef<string | undefined>(undefined);

  const forceStoreSelection =
    forceToSelectStoreOnEachVisit || forceToSelectStore;

  const [stores, setStores] = useState<TreezStore[]>([]);
  const [pickupAllStore, setPickupAllStore] = useState<TreezStore[]>([]);
  const {
    is_crm_modal_open,
    terms_accepted,
    treez_store_name,
    delivery_details,
    delivery_address,
    temporary_fulfillment_method,
    next_store_id,
    setState,
  } = useEcomStoreSelector([
    'treez_store_name',
    'delivery_details',
    'delivery_address',
    'is_crm_modal_open',
    'terms_accepted',
    'temporary_fulfillment_method',
    'next_store_id',
  ]);

  /**
   * A picked store that `StoreValidator` is cleared to commit, so the auto-open effects below must
   * not ask for one: after a per-location gate, `treez_store_name` is briefly empty while the
   * choice is already made. Same condition `StoreValidator` uses, which also means a `next_store_id`
   * left behind by an abandoned gate doesn't count — nothing will commit it, so the selector opens.
   */
  const isStoreCommitPending =
    !!next_store_id &&
    isStoreAgeGateSatisfied(
      ageGate?.data,
      next_store_id,
      getCookieObject(CookiesNames._STORE_TERMS),
    );
  const areTermsAccepted = !isServerSide && (!ageGate || terms_accepted);
  const { is_store_location_open, setState: setUiState } = useUiStoreSelector([
    'is_store_location_open',
  ]);

  const { singleStore, onlyPickup, onlyDelivery, deliveryAndPickup } =
    useShoppingMode();

  const [isDelivery, setIsDelivery] = useState<boolean>(false);
  const [isPickup, setIsPickup] = useState<boolean>(false);
  const [deliveryAddress, setDeliveryAddress] = useState<
    ILocalAddress | undefined
  >();
  const [storeAddress, setStoreAddress] = useState('');

  const [isDeliveryAddressInvalid, setIsDeliveryAddressInvalid] =
    useState(false);

  const [isPickupInvalid, setIsPickupInvalid] = useState(false);

  const [selectedStoreId, setSelectedStoreId] = useState<string>('');

  const [showOverlay, setShowOverlay] = useState(false);

  // Sync cookie when the committed store (Zustand) changes — same pattern as production,
  // but on treez_store_name (commit) instead of selectedStoreId (radio).
  useEffect(() => {
    if (!treez_store_name) {
      return;
    }

    setCookieValue(CookiesNames._SELECTED_STORE_ID, treez_store_name);

    if (prevCommittedStoreRef.current === undefined) {
      prevCommittedStoreRef.current = treez_store_name;
      return;
    }

    if (prevCommittedStoreRef.current !== treez_store_name) {
      prevCommittedStoreRef.current = treez_store_name;
      notifyStoreCookieChanged(treez_store_name);
    }
  }, [treez_store_name, setCookieValue]);

  useEffect(() => {
    // No document means we can't conclude anything — never mark on missing data.
    if (!treez_store_name || !ageGate?.data) return;

    const storeTerms = getCookieObject<StoreTermsMap>(
      CookiesNames._STORE_TERMS,
    );
    // Already marked → don't rewrite, it would re-date every other entry in the map.
    if (storeTerms?.[treez_store_name]) return;
    if (resolveAgeGateContent(ageGate?.data, treez_store_name).matched) return;

    const globalTerms = getCookieObject<IAgeTermsData>(
      CookiesNames._TERMS_ACCEPTED,
    );

    setCookieObject(
      CookiesNames._STORE_TERMS,
      addStoreTerm(storeTerms, treez_store_name),
      { maxAge: resolveStoreTermsMaxAge(globalTerms?.terms_accepted_exp_date) },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treez_store_name, ageGate]);

  useEffect(() => {
    if (!is_store_location_open) {
      setShowOverlay(false);
    }
  }, [is_store_location_open]);

  const pickupSectionTitle =
    storeSelector?.pickup_section_title ?? 'Select a pickup location';

  const deliverySectionTitle =
    storeSelector?.delivery_section_title ?? 'Enter delivery address';

  const title = storeSelector?.title ?? 'How do you want to shop?';

  const buttonLabel = storeSelector?.button_label ?? 'How do you want to shop?';

  const buttonDisplayInfo = storeSelector?.button_display_info ?? 'Address';

  const mainButtonType = storeSelector?.main_button_type ?? 'primary';

  const pickupMainButtonText =
    storeSelector?.pickup_main_button_text ?? 'SHOP PICKUP';

  const deliveryMainButtonText =
    storeSelector?.delivery_main_button_text ?? 'SHOP DELIVERY';

  const secondaryButtonType =
    storeSelector?.secondary_button_type ?? 'secondary';

  const secondaryButtonText =
    storeSelector?.secondary_button_text ?? 'VIEW ALL LOCATIONS';

  const secondaryButtonLink =
    storeSelector?.secondary_button_link ?? '/store-locator';

  const hideSecondaryButton = storeSelector?.hide_secondary_button;

  const hideIconSecondaryButton = storeSelector?.hide_icon_secondary_button;

  const showSearchBox = storeSelector?.display_searchbox || false;

  const showFilterTabs = !!storeSelector?.filter_tabs_active;

  const enableDeliveryZoneRouting =
    !!storeSelector?.enable_delivery_zone_routing;

  const allFilterTabLabel = storeSelector?.all_tab_label?.trim() || 'All';

  const filterTabItems = storeSelector?.tab_filters;

  useLayoutEffect(() => {
    if (isServerSide) return;
    if (
      forceToSelectStoreOnEachVisit &&
      !treez_store_name &&
      !isStoreCommitPending
    ) {
      setState({
        temporary_fulfillment_method: null,
        delivery_address: undefined,
      });
      setShowOverlay(true);
      setUiState({ is_store_location_open: true });
    }
  }, [
    forceToSelectStoreOnEachVisit,
    treez_store_name,
    isStoreCommitPending,
    isServerSide,
  ]);

  // Clear store selection when the app loads in a new tab (tab close clears sessionStorage;
  // in-tab navigation keeps it, so we don't clear when navigating in the URL).
  useEffect(() => {
    if (
      !forceToSelectStoreOnEachVisit ||
      isServerSide ||
      typeof sessionStorage === 'undefined'
    ) {
      return;
    }

    const SESSION_KEY = '@store_locator/session';

    if (!sessionStorage.getItem(SESSION_KEY)) {
      setState({ treez_store_name: '' });
    }
    sessionStorage.setItem(SESSION_KEY, '1');
  }, [setState, isServerSide]);

  useEffect(() => {
    // if the store offers pickup and delivery, select pickup by default, if it offer only
    // delivery, select delivery by default
    const storeshort_name = treezStores[0].short_name;

    if (singleStore && !forceStoreSelection) {
      let didSetStore = false;

      setState(state => {
        if (
          !state.delivery_details.pickup &&
          !state.delivery_details.delivery
        ) {
          didSetStore = true;
          return {
            delivery_details: {
              delivery: onlyDelivery,
              pickup: !onlyDelivery,
            },
            treez_store_name: storeshort_name,
          };
        }
        return state;
      });

      if (didSetStore) {
        measureSurfsideChangeLocation(storeshort_name);
      }
    }
  }, []);

  // this is a feature flag that forces the user
  // to select a store
  useEffect(() => {
    if (forceStoreSelection) {
      // this is a feature flag
      if (
        !treez_store_name && // not store selected
        !isStoreCommitPending && // a picked store is already cleared and about to commit
        !is_crm_modal_open && // cmr modal is not open
        areTermsAccepted // age gate accepted
      ) {
        setShowOverlay(true);
        setUiState({ is_store_location_open: true });
      }
    }
  }, [
    is_store_location_open,
    areTermsAccepted,
    is_crm_modal_open,
    forceStoreSelection,
    singleStore,
    treez_store_name,
    isStoreCommitPending,
  ]);

  useEffect(() => {
    if (!is_store_location_open) {
      setState({ temporary_fulfillment_method: null });
    }

    if (temporary_fulfillment_method) {
      setIsDelivery(temporary_fulfillment_method === 'DELIVERY');
      setIsPickup(temporary_fulfillment_method === 'PICKUP');
      return;
    }
    // pickup takes precedence over delivery when both flags on delivery_details are active
    // NOTE: if delivery_details.pickup/delivery should NOT prioritize pickup when both are active,
    // remove this block and let delivery-check execute first instead.
    if (onlyPickup || delivery_details.pickup) {
      setIsPickup(true);
      setIsDelivery(false);
      return;
    }

    if (onlyDelivery || delivery_details.delivery) {
      setIsPickup(false);
      setIsDelivery(true);
      return;
    }

    setIsPickup(true);
    setIsDelivery(false);
  }, [
    delivery_details.delivery,
    delivery_details.pickup,
    onlyDelivery,
    onlyPickup,
    is_store_location_open,
    temporary_fulfillment_method,
  ]);

  useEffect(() => {
    // by default, select the only available store
    if (
      singleStore &&
      !treez_store_name &&
      forceStoreSelection &&
      !onlyDelivery
    ) {
      const storeshort_name = treezStores[0].short_name;
      setSelectedStoreId(storeshort_name);
      return;
    }

    if (isDelivery && selectedStoreId) {
      return;
    }

    setSelectedStoreId(treez_store_name);
  }, [treez_store_name, is_store_location_open]);

  useEffect(() => {
    if (delivery_details?.delivery) {
      setDeliveryAddress(delivery_address as ILocalAddress);
    }
  }, [delivery_address]);

  // filter the stores based on the shopping mode and
  // always show the selected store first
  useEffect(() => {
    if (isDelivery && treez_store_name) {
      if (enableDeliveryZoneRouting && storeHasConfiguredDeliveryZones(store)) {
        const providerIds = getOverlappingDeliveryProviderIds(
          deliveryAddress,
          store,
          treezStores,
        );

        if (providerIds.length) {
          setSelectedStoreId(
            getDeliveryProviderId(providerIds, selectedStoreId)!,
          );
        }
      } else {
        const deliverySchelude = getDeliveryScheduleByAddress(
          deliveryAddress!,
          store,
          treezStores,
        );

        if (deliverySchelude && !!deliverySchelude?.stores_provider_id) {
          setSelectedStoreId(deliverySchelude?.stores_provider_id);
        }
      }
    } else if (isPickup && selectedStoreId && !!treez_store_name) {
      setSelectedStoreId(treez_store_name);
    }

    const filteredStores = treezStores.filter(store =>
      isDelivery ? store.delivery : store.pickup,
    );

    const t = filteredStores.find(
      store => store.short_name === selectedStoreId,
    );

    if (!t) {
      setStores(filteredStores);
      if (isPickup) {
        setPickupAllStore(mergeStoresWithFlag(filteredStores, externalStores));
      }
      return;
    }

    const stores = [
      t,
      ...filteredStores.filter(store => store.short_name !== selectedStoreId),
    ];

    setStores(stores);

    if (isPickup) {
      setPickupAllStore(mergeStoresWithFlag(stores, externalStores));
    }
  }, [
    deliveryAddress,
    enableDeliveryZoneRouting,
    is_store_location_open,
    isDelivery,
    isPickup,
    store,
    treezStores,
    treez_store_name,
  ]);

  return (
    <StoreLocatorContext
      value={{
        deliveryAddress: deliveryAddress as ILocalAddress,
        deliveryAndPickup,
        isDelivery,
        isDeliveryAddressInvalid,
        isPickup,
        isPickupInvalid,
        onlyDelivery,
        onlyPickup,
        selectedStoreId,
        setDeliveryAddress,
        setIsDelivery,
        setIsDeliveryAddressInvalid,
        setIsPickup,
        setIsPickupInvalid,
        setSelectedStoreId,
        setStoreAddress,
        setStores,
        singleStore,
        storeAddress,
        stores,
        externalStores,
        showOverlay,
        setShowOverlay,
        isDeliveryFromEconStore: !!delivery_details?.delivery,
        title,
        buttonLabel,
        buttonDisplayInfo,
        pickupSectionTitle,
        deliverySectionTitle,
        mainButtonType,
        pickupMainButtonText,
        deliveryMainButtonText,
        secondaryButtonType,
        secondaryButtonText,
        secondaryButtonLink,
        hideSecondaryButton,
        hideIconSecondaryButton,
        showSearchBox,
        pickupAllStore,
        showFilterTabs,
        allFilterTabLabel,
        filterTabItems,
        enableDeliveryZoneRouting,
      }}
    >
      <div
        className={classNames(style.store_locator__container, {
          [style.store_locator__container__centered_single_row]:
            activeSlice?.variation === 'withCenteredLogoAndLocatorFix',
          [style.store_locator__container__full_width]: fullWidth,
        })}
      >
        {children}
      </div>
    </StoreLocatorContext>
  );
};

export { StoreLocatorProvider };
